import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

interface ChannelFormData {
    name: string;
    url: string;
    active: boolean;
    full_scan: boolean;
    language: string;
    description?: string;
    youtube_channel_id?: string;
    subscriber_count?: number;
    verified: boolean;
}

export const AdminAddChannelPage: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const [loading, setLoading] = useState(false);
    const [urlValidation, setUrlValidation] = useState<{
        status: 'idle' | 'loading' | 'valid' | 'invalid';
        message: string;
    }>({ status: 'idle', message: '' });
    const [formData, setFormData] = useState<ChannelFormData>({
        name: '',
        url: '',
        active: true,
        full_scan: true,
        language: 'en',
        description: '',
        youtube_channel_id: '',
        subscriber_count: undefined,
        verified: false
    });

    // URL validation effect
    useEffect(() => {
        const validateUrl = async () => {
            const url = formData.url.trim();

            if (!url) {
                setUrlValidation({ status: 'idle', message: '' });
                return;
            }

            // Show loading state
            setUrlValidation({ status: 'loading', message: 'Checking URL...' });

            // Debounce validation
            const timeoutId = setTimeout(async () => {
                try {
                    // Check if it's a valid YouTube channel URL
                    const youtubeChannelRegex = /^https?:\/\/(www\.)?youtube\.com\/(@[\w-]+|channel\/[\w-]+|c\/[\w-]+|user\/[\w-]+)/i;

                    if (!youtubeChannelRegex.test(url)) {
                        setUrlValidation({ status: 'invalid', message: 'Invalid YouTube channel URL' });
                        return;
                    }

                    // Extract channel ID
                    const channelId = extractChannelId(url);
                    if (!channelId) {
                        setUrlValidation({ status: 'invalid', message: 'Could not extract channel ID from URL' });
                        return;
                    }

                    // Check if channel already exists
                    console.log('Checking channel URL:', url);
                    
                    const response = await fetch('/api/admin/check-channel', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({ url })
                    });

                    const data = await response.json();
                    console.log('Channel check response:', data);

                    if (data.exists) {
                        setUrlValidation({ status: 'invalid', message: 'Channel already exists in database ❌' });
                    } else {
                        setUrlValidation({ status: 'valid', message: 'Channel URL is valid and available! ✅' });
                    }
                } catch (error) {
                    console.error('Error validating URL:', error);
                    setUrlValidation({ status: 'invalid', message: 'Error checking channel availability' });
                }
            }, 500);

            return () => clearTimeout(timeoutId);
        };

        validateUrl();
    }, [formData.url]);

    const extractChannelId = (url: string): string | null => {
        const patterns = [
            /youtube\.com\/@([\w-]+)/i,
            /youtube\.com\/channel\/([\w-]+)/i,
            /youtube\.com\/c\/([\w-]+)/i,
            /youtube\.com\/user\/([\w-]+)/i
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) {
                return match[1];
            }
        }
        return null;
    };

    // Check if user is admin (handle both boolean and numeric values)
    const isAdmin = Boolean(user?.is_admin);
    if (!isAuthenticated || !user || !isAdmin) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
                    <p className="text-gray-600 dark:text-gray-400">You need admin privileges to access this page.</p>
                </div>
            </div>
        );
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.url.trim()) {
            toast.error('Name and URL are required');
            return;
        }

        // Check if URL is valid
        if (urlValidation.status !== 'valid') {
            toast.error('Please enter a valid YouTube channel URL');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('/api/admin/channels', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include', // Include cookies for authentication
                body: JSON.stringify({
                    name: formData.name.trim(),
                    url: formData.url.trim(),
                    active: formData.active ? 1 : 0,
                    full_scan: formData.full_scan ? 1 : 0,
                    language: formData.language,
                    description: formData.description?.trim() || null,
                    youtube_channel_id: formData.youtube_channel_id?.trim() || null,
                    subscriber_count: formData.subscriber_count || null,
                    verified: formData.verified ? 1 : 0
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add channel');
            }

            const result = await response.json();

            toast.success(`Channel "${formData.name}" added successfully!`);

            // Reset form
            setFormData({
                name: '',
                url: '',
                active: true,
                full_scan: true,
                language: 'en',
                description: '',
                youtube_channel_id: '',
                subscriber_count: undefined,
                verified: false
            });

        } catch (error) {
            console.error('Error adding channel:', error);
            toast.error(error instanceof Error ? error.message : 'Failed to add channel');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Add New Channel
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Add a new YouTube channel to the recap database
                    </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Channel Name *
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="e.g., Respect"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="url" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    YouTube URL *
                                </label>
                                <input
                                    type="url"
                                    id="url"
                                    name="url"
                                    value={formData.url}
                                    onChange={handleInputChange}
                                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${urlValidation.status === 'valid'
                                            ? 'border-green-500 focus:ring-green-500'
                                            : urlValidation.status === 'invalid'
                                                ? 'border-red-500 focus:ring-red-500'
                                                : 'border-gray-300 dark:border-gray-600 focus:ring-purple-500'
                                        }`}
                                    placeholder="https://www.youtube.com/@MrRespect288/videos"
                                    required
                                />
                                {urlValidation.status !== 'idle' && (
                                    <div className={`mt-2 flex items-center text-sm ${urlValidation.status === 'valid'
                                            ? 'text-green-600 dark:text-green-400'
                                            : urlValidation.status === 'invalid'
                                                ? 'text-red-600 dark:text-red-400'
                                                : 'text-yellow-600 dark:text-yellow-400'
                                        }`}>
                                        <span className="mr-2">
                                            {urlValidation.status === 'valid' && '✅'}
                                            {urlValidation.status === 'invalid' && '❌'}
                                            {urlValidation.status === 'loading' && '⏳'}
                                        </span>
                                        {urlValidation.message}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Language and Status */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label htmlFor="language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Language
                                </label>
                                <select
                                    id="language"
                                    name="language"
                                    value={formData.language}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="en">English</option>
                                    <option value="es">Spanish</option>
                                    <option value="pt">Portuguese</option>
                                    <option value="fr">French</option>
                                    <option value="de">German</option>
                                    <option value="it">Italian</option>
                                    <option value="ja">Japanese</option>
                                    <option value="ko">Korean</option>
                                    <option value="zh">Chinese</option>
                                    <option value="und">Undefined</option>
                                </select>
                            </div>

                            <div className="flex items-center space-x-4">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="active"
                                        checked={formData.active}
                                        onChange={handleInputChange}
                                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Active</span>
                                </label>

                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="verified"
                                        checked={formData.verified}
                                        onChange={handleInputChange}
                                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Verified</span>
                                </label>
                            </div>

                            <div>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        name="full_scan"
                                        checked={formData.full_scan}
                                        onChange={handleInputChange}
                                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Full Scan</span>
                                </label>
                            </div>
                        </div>

                        {/* Additional Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="youtube_channel_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    YouTube Channel ID
                                </label>
                                <input
                                    type="text"
                                    id="youtube_channel_id"
                                    name="youtube_channel_id"
                                    value={formData.youtube_channel_id}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="e.g., UC..."
                                />
                            </div>

                            <div>
                                <label htmlFor="subscriber_count" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Subscriber Count
                                </label>
                                <input
                                    type="number"
                                    id="subscriber_count"
                                    name="subscriber_count"
                                    value={formData.subscriber_count || ''}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="e.g., 1000000"
                                    min="0"
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Description
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="Channel description (optional)"
                            />
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-end space-x-4">
                            <button
                                type="button"
                                onClick={() => window.history.back()}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 dark:text-white dark:bg-gray-600 dark:hover:bg-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Adding...' : 'Add Channel'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}; 